import { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Headset,
  Star,
  FileCheck2,
  Building2,
  Clock4,
  Calendar1,
  DollarSign,
  Clipboard,
  ChartPie,
  Clock5,
} from "lucide-react";

import { DeclineOpportunityDialog } from "../dialogs/opportunity/decline";
import { useAvailabilitySheet } from "../dialogs/candidate/availability/store";
import ChatWindow, { ChatWindowDialogHandle } from "../forms/chat";
import { usePaymentInfoDialogState } from "../dialogs/payment-info/store";
import { ViewPaymentsCandidate } from "../dialogs/opportunity/payments";
import { ViewMilestonesCandidate } from "../dialogs/opportunity/milestones";

import { useCandidateAvailabilityQuery } from "@/queries/candidate/availability";
import { useConversationsQuery } from "@/queries/chat/user-conversation";
import { useProfileQuery } from "@/queries/user/profile";
import { Opportunity } from "@/queries/candidate/opportunities";
import { useStripeAccountQuery } from "@/queries/stripe";

import formatBudgetRange from "@/utils/formatBudgetRange";
import dateStringToLocalDate from "@/utils/dateStringToLocalDate";
import { dateToFriendlyPostedDate } from "@/utils/dateToFriendlyPostedDate";

import { CandidateInterview } from "@/types";

export const OpportunityRow = ({
  opportunity,
  highlight,
  handleApplyClick,
  handleDetailsClick,
}: {
  opportunity: Opportunity;
  highlight?: boolean;
  handleApplyClick: (o: Opportunity) => void;
  handleDetailsClick: (o: Opportunity) => void;
}) => {
  const { profile } = useProfileQuery();
  const { chatConversations } = useConversationsQuery();

  const chatRef = useRef<ChatWindowDialogHandle>(null);

  const candidateStatusText = useMemo(() => {
    if (!opportunity.candidateStatus?.status) {
      return;
    }

    switch (opportunity.candidateStatus.status) {
      case "Identified":
      case "Initial Outreach":
        return;
      case "Checking Interest":
        return "Matched";
      case "Not Interested":
        return "Not Interested";
      case "Rejected for Role":
        return "Declined";
      case "Accepted by Client":
        return "Hired";
      default:
        return "Applied";
    }
  }, [opportunity]);

  const mostRecentInterview = useMemo(
    () => opportunity.candidateStatus?.interviews.at(-1),
    [opportunity.candidateStatus?.interviews],
  );

  // See if there is an existing conversation between the user and the company
  const conversation = useMemo(
    () =>
      chatConversations?.find(
        (conversation) =>
          conversation.jobOpeningId === opportunity.id &&
          conversation.participants.length === 2 &&
          conversation.participants.every(
            (participant) =>
              participant.companyId === opportunity.company?.id ||
              participant.userId === profile?.id,
          ),
      ),
    [chatConversations, opportunity.id, opportunity.company?.id, profile?.id],
  );

  const hasMessageWaiting = useMemo(
    () =>
      // The conversation has at least one message
      conversation?.lastMessage &&
      // User hasn't seen any of the messages
      (!conversation.lastSeenAt ||
        // Or they haven't seen the most recent one (at least)
        conversation.lastMessage.createdAt > conversation.lastSeenAt),
    [conversation],
  );

  const chatDisabled =
    opportunity.status === "Role Filled (lost)" ||
    opportunity.status === "Role Filled (won)";

  const participants =
    profile?.id && opportunity.company?.id
      ? {
          userId: profile.id,
          companyId: opportunity.company.id,
        }
      : undefined;

  const formattedBudget =
    formatBudgetRange(opportunity.budgetMinCents, opportunity.budgetMaxCents) ??
    opportunity.budgetNotes;

  let budgetString: string | undefined;
  if (!formattedBudget) {
    // No-op
  } else if (opportunity.type === "Project" && opportunity.budgetType) {
    budgetString = `${formattedBudget} / ${opportunity.budgetType === "Monthly retainer" ? "mo retainer" : "project"}`;
  } else if (opportunity.type === "Fractional") {
    budgetString = `${formattedBudget} / mo`;
  } else if (opportunity.type === "Full-time") {
    budgetString = `${formattedBudget} / yr`;
  }

  return (
    <div className="w-full flex flex-col px-6 p-4 border-b last-of-type:border-b-0">
      <div className="w-full flex flex-col gap-2 md:flex-row justify-between md:items-center flex-wrap">
        <div className="flex flex-row gap-4">
          {highlight && (
            <Star className="text-secondary fill-secondary size-4" />
          )}
          <img
            className="size-12 rounded-full"
            src={opportunity.company?.logoLink}
            alt={`${opportunity.company?.name} Logo`}
          />
          <div className="flex flex-col">
            <div className="flex flex-row md:items-center gap-1 md:gap-2 flex-wrap">
              <h4
                className="text-xl font-bold text-[#0a0a0a] hover:underline cursor-pointer"
                onClick={() => handleDetailsClick(opportunity)}
              >
                {opportunity.role}
              </h4>
              {Boolean(candidateStatusText) && (
                <span className="w-fit rounded-full bg-border/50 border text-xs text-muted-foreground px-2 py-1">
                  {candidateStatusText}
                </span>
              )}
              {hasMessageWaiting && (
                <span
                  className="w-fit rounded-full bg-red-600 text-xs text-white px-2 py-1 cursor-pointer"
                  onClick={() => setTimeout(() => chatRef.current?.open(), 0)}
                >
                  Message Waiting
                </span>
              )}
            </div>
            <div className="flex flex-row gap-2 items-center">
              <Building2 size={16} stroke="#4b5563" />
              <p className="text-base font-normal text-[#4b5563]">
                {opportunity.company?.name}
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-row flex-wrap gap-2 md:gap-4 items-center md:ml-16">
          {opportunity.status !== "Role Filled (won)" &&
            opportunity.status !== "Role Filled (lost)" &&
            opportunity.candidateStatus &&
            opportunity.candidateStatus.status !== "Rejected for Role" &&
            opportunity.candidateStatus.status !== "Not Interested" &&
            opportunity.candidateStatus.status !== "Contracting" &&
            opportunity.candidateStatus.status !== "Accepted by Client" && (
              <DeclineOpportunityDialog
                openingId={opportunity.id}
                candidateStatusId={opportunity.candidateStatus.id}
                button={
                  <Button variant="outline">
                    {opportunity.candidateStatus.status === "Checking Interest"
                      ? "Decline"
                      : "Withdraw"}
                  </Button>
                }
              />
            )}
          {(conversation ?? participants) &&
            opportunity.candidateStatus &&
            opportunity.candidateStatus.status !== "Identified" &&
            opportunity.candidateStatus.status !== "Initial Outreach" &&
            opportunity.candidateStatus.status !== "Checking Interest" && (
              <ChatWindow
                conversation={conversation}
                participants={participants}
                jobOpeningId={opportunity.id}
                /* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */
                title={`Chat with ${opportunity.company?.name || "Company"}`}
                disabled={chatDisabled}
                ref={chatRef}
              />
            )}
          <OpportunityButtons
            opportunity={opportunity}
            handleApplyClick={handleApplyClick}
            handleDetailsClick={handleDetailsClick}
          />
        </div>
      </div>
      <div className="md:ml-16 mt-4 md:mt-2 flex flex-col gap-4">
        {Boolean(opportunity.shortDescription) && (
          <p className="text-base font-normal text-[#374151] whitespace-break-spaces overflow-hidden line-clamp-3">
            {opportunity.shortDescription}
          </p>
        )}
        <div className="flex flex-row flex-wrap gap-4 self-stretch items-center">
          {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
          {opportunity.scopeTime || opportunity.scopeEngagementDuration ? (
            <div className="flex flex-row gap-2 items-center">
              <Clock4 className="stroke-muted-foreground" size={16} />
              <span className="text-sm text-muted-foreground">
                {[opportunity.scopeTime, opportunity.scopeEngagementDuration]
                  .filter((v) => Boolean(v))
                  .join(" | ")}
              </span>
            </div>
          ) : undefined}
          {opportunity.desiredStartDate && (
            <div className="col-span-2 flex flex-row gap-2 items-center">
              <Calendar1 className="stroke-muted-foreground" size={16} />
              <span className="text-sm text-muted-foreground">
                {dateStringToLocalDate(
                  opportunity.desiredStartDate,
                )?.toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
            </div>
          )}
          {Boolean(budgetString) && (
            <div className="col-span-2 flex flex-row gap-2 items-center">
              <DollarSign className="stroke-muted-foreground" size={16} />
              <span className="text-sm text-muted-foreground">
                {budgetString}
              </span>
            </div>
          )}
          <span
            className={cn(
              "w-fit px-2 py-1",
              "flex flex-row items-center gap-1",
              "text-xs font-semibold",
              opportunity.type === "Fractional"
                ? "bg-purple-200 text-purple-600 stroke-purple-600"
                : opportunity.type === "Project"
                  ? "bg-green-200 text-green-600 stroke-green-600"
                  : opportunity.type === "Full-time"
                    ? "bg-blue-200 text-blue-600 stroke-blue-600"
                    : undefined,
            )}
          >
            {opportunity.type === "Fractional" ? (
              <>
                <ChartPie size={12} />
                Fractional
              </>
            ) : opportunity.type === "Project" ? (
              <>
                <Clipboard size={12} />
                Project
              </>
            ) : opportunity.type === "Full-time" ? (
              <>
                <Clock5 size={12} />
                Full-time
              </>
            ) : undefined}
          </span>
          {mostRecentInterview && (
            <span className="flex flex-row items-center gap-1">
              <p className="text-sm font-semibold text-orange-600">
                {mostRecentInterview.isCompleted
                  ? // If the most recent interview is complete, report as much
                    `Interview "${mostRecentInterview.name}" Complete`
                  : // If the interview is incomplete and not scheduled
                    !mostRecentInterview.scheduled
                    ? `Interview "${mostRecentInterview?.name}" Requested (Awaiting Scheduling)`
                    : // If the interview is scheduled but we don't know when (shouldn't happen)
                      !mostRecentInterview.startTime
                      ? `Interview "${mostRecentInterview.name}" Scheduled`
                      : // If the interview is scheduled with a date
                        `Interview "${mostRecentInterview.name}" Scheduled on ${new Date(mostRecentInterview.startTime).toLocaleString("en-US")}`}
              </p>
            </span>
          )}
        </div>
      </div>
      <div className="col-span-2 flex flex-row gap-2 items-center self-end">
        <span className="text-sm text-muted-foreground">
          {dateToFriendlyPostedDate(opportunity.createdAt)}
        </span>
      </div>
    </div>
  );
};

const OpportunityButtons = ({
  opportunity,
  handleApplyClick,
  handleDetailsClick,
}: {
  opportunity: Opportunity;
  handleApplyClick: (o: Opportunity) => void;
  handleDetailsClick: (o: Opportunity) => void;
}) => {
  const { stripeAccount } = useStripeAccountQuery();
  const { setOpen, setOpportunity, setInterview } = useAvailabilitySheet();

  const [currentInterview, setCurrentInterview] =
    useState<CandidateInterview | null>(null);

  useEffect(() => {
    const incompleteInterview = opportunity.candidateStatus?.interviews.find(
      (interview) => !interview.isCompleted,
    );

    setCurrentInterview(incompleteInterview ?? null);
  }, [opportunity]);

  const { candidateAvailability } = useCandidateAvailabilityQuery(
    opportunity.id,
    currentInterview?.id ?? "",
  );

  const hasCandidateAvailability = useMemo(
    () =>
      candidateAvailability?.general.some(
        (day) => day.some(({ placeholder }) => !placeholder),
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      ) || Boolean(candidateAvailability?.specific.length),
    [candidateAvailability],
  );

  const offer = opportunity.candidateStatus?.offers.at(-1);
  const billing = opportunity.candidateStatus?.billing;

  const buttons = [
    <Button
      key={`${opportunity.id}-view`}
      variant={(offer ?? billing) ? "outline" : "secondary"}
      onClick={() => handleDetailsClick(opportunity)}
    >
      View Details
    </Button>,
  ];

  if (!opportunity.candidateStatus) {
    return buttons;
  }

  if (opportunity.candidateStatus.status === "Checking Interest") {
    buttons.push(
      <Button
        key={`${opportunity.id}-apply`}
        variant="secondary"
        onClick={() => handleApplyClick(opportunity)}
      >
        <Clipboard />
        Apply
      </Button>,
    );
  }

  if (
    opportunity.candidateStatus.status === "Screening" ||
    opportunity.candidateStatus.status === "Possible Fit" ||
    opportunity.candidateStatus.status === "Shortlisted"
  ) {
    const interviewFocus = currentInterview && !currentInterview.scheduled;
    buttons.push(
      <Button
        key={`${opportunity.id}-interview`}
        variant={interviewFocus ? "secondary" : "outline"}
        onClick={() => {
          if (currentInterview?.scheduled && currentInterview.link) {
            window.open(currentInterview.link, "_blank");
          } else if (currentInterview) {
            setOpportunity(opportunity);
            setInterview(currentInterview);
            setOpen(true);
          }
        }}
        disabled={
          !currentInterview ||
          (currentInterview.scheduled && !currentInterview.link)
        }
      >
        <Headset />
        {!currentInterview
          ? "No Interview Requested"
          : currentInterview?.scheduled
            ? currentInterview.link
              ? "Join Interview"
              : "Interview Scheduled"
            : hasCandidateAvailability
              ? "Edit Availability"
              : "Add Availability"}
      </Button>,
    );
  }

  if (opportunity.candidateStatus.status === "Accepted by Client") {
    buttons.push(
      <Button
        key={`${opportunity.id}-contract`}
        variant="outline"
        disabled={!opportunity.contracts?.[0]}
        onClick={() => {
          if (opportunity.contracts?.[0]) {
            window.open(opportunity.contracts[0].link, "_blank");
          }
        }}
      >
        <FileCheck2 />
        View Contract
      </Button>,
    );
  }

  if (offer) {
    if (offer.status !== "accepted") {
      buttons.push(
        <Button
          key={`${opportunity.id}-offers`}
          variant="secondary"
          onClick={() => handleDetailsClick(opportunity)}
        >
          Review offer
        </Button>,
      );
    } else if (!stripeAccount || stripeAccount.status !== "ready") {
      buttons.push(
        <Button
          key={`${opportunity.id}-offers`}
          variant="secondary"
          onClick={() => usePaymentInfoDialogState.getState().setOpen(true)}
        >
          Set up direct deposit
        </Button>,
      );
    } else {
      buttons.push(
        <Button
          key={`${opportunity.id}-offers`}
          variant={
            opportunity.candidateStatus.billing ? "outline" : "secondary"
          }
          onClick={() => usePaymentInfoDialogState.getState().setOpen(true)}
        >
          Review direct deposit
        </Button>,
      );
    }
  }

  if (opportunity.candidateStatus.billing) {
    buttons.push(
      <ViewPaymentsCandidate
        key={`${opportunity.id}-payments`}
        candidate={opportunity.candidateStatus}
      />,
      <ViewMilestonesCandidate
        key={`${opportunity.id}-milestones`}
        jobOpeningId={opportunity.id}
        candidate={opportunity.candidateStatus}
      />,
    );
  }

  return buttons;
};
